import { conBaseLocal, STORE_PENDING_SOFTYS } from './db';
import type {
  ContextoVisitaSoftys, TipoVisitaSoftys, UnidadPedido,
} from '../api/softys';

/**
 * Cola local de las visitas de Softys (Toallitas Húmedas · Babysec).
 *
 * POR QUÉ EXISTE, y acá más que en ninguna otra: la campaña recorre **un mercado por día** y el
 * 14-set esos mercados son Iquitos, Juliaca, Tacna, Andahuaylas, Tumbes y Huánuco. Adentro de un
 * mercado de abastos la señal es mala aun en Lima. Si registrar un puesto exigiera conexión, la
 * jornada entera se perdería y no por el diseño del formulario.
 *
 * POR QUÉ ES UN STORE APARTE y no una generalización: mismo criterio que canjes y visitas. La
 * cola de asistencia es el camino más crítico del sistema y ya está en producción con gente
 * marcando de verdad; generalizarla para una forma de dato nueva es arriesgar el marcado para
 * ahorrar un archivo. Se replica el patrón probado: idempotencia por `clientUuid`, `attempts` que
 * solo cuentan los rechazos definitivos, y `status` `pendiente`/`fallido`.
 */

/** La visita tal como se guarda localmente: sin las rutas, que salen al subir las fotos. */
export interface VisitaSoftysParaRegistrar {
  clientUuid: string;
  tipo: TipoVisitaSoftys;
  contexto: ContextoVisitaSoftys;
  ocurridoEn: string;
  puestoNumero: string;
  puestoNombre: string;
  comercianteDni: string | null;
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  /** Calidad del dato, calculada en la pantalla. Viaja por la cola porque se decide al REGISTRAR:
   *  si se recalculara al enviar, una visita sin GPS que sale de la cola dos horas después podría
   *  salir con la coordenada del lugar donde la persona recuperó la señal. */
  flags?: string[];
  /** `false` cuando se cargó para otra fecha: la hora es aproximada. Sin la clave = hora real. */
  horaReal?: boolean;
  premio: string | null;
  hizoPedido?: boolean | null;
  pedidoUnidad?: UnidadPedido | null;
  pedidoCantidad?: number | null;
  escuchoCapacitacion?: boolean | null;
  /** Solo en las campañas que lo preguntan (0334). Viajan tal cual hasta `ficha`. */
  muestraEntregada?: boolean | null;
  bonificacionPacks?: number | null;
  dinamicaMayorista?: string | null;
  promociones?: number | null;
}

export interface PendingVisitaSoftys {
  clientUuid: string;
  input: VisitaSoftysParaRegistrar;
  /**
   * La foto de la FACHADA del puesto. Es la evidencia de que se estuvo ahí y va en cada registro,
   * por decisión del usuario (13-set). Se guarda el blob porque la subida puede no tener red.
   */
  fachadaBlob: Blob | null;
  /** Solo en la visita a comerciante, y opcional: la foto del pedido. */
  pedidoBlob: Blob | null;
  /** Solo en la visita a comerciante: la foto de salida. */
  salidaBlob: Blob | null;
}

export interface QueuedVisitaSoftys extends PendingVisitaSoftys {
  attempts: number;
  lastError: string | null;
  status: 'pendiente' | 'fallido';
}

export const MAX_SOFTYS_ATTEMPTS = 5;

export interface SoftysUploader {
  subirFotoVisitaSoftys(
    clientUuid: string, tipo: 'fachada' | 'pedido' | 'salida', foto: Blob,
  ): Promise<string>;
  registrarVisitaSoftys(input: Record<string, unknown>): Promise<string>;
  isPermanentError(err: unknown): boolean;
}

let uploader: SoftysUploader | null = null;

export function setSoftysUploader(u: SoftysUploader): void {
  uploader = u;
}

/** Guarda la visita en la cola. Idempotente por `clientUuid`. */
export async function enqueueVisitaSoftys(item: PendingVisitaSoftys): Promise<void> {
  await conBaseLocal(async (db) => {
    const existing = await db.get(STORE_PENDING_SOFTYS, item.clientUuid);
    await db.put(STORE_PENDING_SOFTYS, {
      ...item,
      attempts: existing?.attempts ?? 0,
      lastError: existing?.lastError ?? null,
      status: existing?.status ?? 'pendiente',
    });
  });
}

/**
 * 🔴 GUARDAR NO PUEDE DEPENDER DE QUE LA BASE LOCAL ESTÉ VIVA.
 *
 * Esta pantalla encolaba SIEMPRE y recién después vaciaba. Es lo correcto para un mercado sin
 * señal —lo escrito no se pierde—, pero convierte a IndexedDB en un requisito duro: el 14-set a
 * las 10:49, en el Mercado Polvos Rosados de Tacna, Safari mató la conexión y la pantalla contestó
 * «Connection to Indexed Database server lost» **con 4G en la barra**. O sea: había con qué
 * mandarlo y no se mandó.
 *
 * El marcado de asistencia nunca sufrió esto porque hace lo contrario (`submitOrQueue`: red
 * primero, cola si falla). Acá el orden se invierte solo cuando la cola no está disponible, que es
 * lo mejor de los dos: sigue siendo a prueba de mercados sin señal, y deja de romperse cuando el
 * navegador se lleva puesta su propia base.
 *
 * Devuelve qué pasó, porque la pantalla tiene que poder decirlo distinto: «guardado en el
 * teléfono» no es lo mismo que «enviado sin respaldo local».
 */
export async function guardarVisitaSoftys(
  item: PendingVisitaSoftys,
): Promise<'encolada' | 'enviada_directo'> {
  try {
    await enqueueVisitaSoftys(item);
    return 'encolada';
  } catch (errCola) {
    if (!uploader) throw errCola;
    try {
      const fotoFachadaPath = item.fachadaBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'fachada', item.fachadaBlob)
        : null;
      const fotoPedidoPath = item.pedidoBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'pedido', item.pedidoBlob)
        : null;
      const fotoSalidaPath = item.salidaBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'salida', item.salidaBlob)
        : null;
      await uploader.registrarVisitaSoftys({
        ...item.input,
        clientUuid: item.clientUuid,
        fotoFachadaPath,
        fotoPedidoPath,
        fotoSalidaPath,
      });
      return 'enviada_directo';
    } catch (errRed) {
      // Se nombran las DOS fallas a propósito: «no hay señal» y «el navegador se llevó puesta su
      // base» se arreglan de forma distinta, y un mensaje que las mezcla manda a probar lo que no
      // es. Lo escrito sigue en pantalla, así que reintentar no cuesta el trabajo de nuevo.
      throw new Error(
        'No se pudo guardar en el teléfono ni enviar al servidor. '
        + 'Memoria del navegador: ' + errorMessage(errCola) + '. '
        + 'Envío: ' + errorMessage(errRed) + '. '
        + 'Recarga la página y vuelve a intentar: lo que escribiste sigue acá.',
      );
    }
  }
}

export async function listPendingSoftys(): Promise<QueuedVisitaSoftys[]> {
  return conBaseLocal(async (db) =>
    (await db.getAll(STORE_PENDING_SOFTYS)).filter((i) => i.status !== 'fallido'));
}

export async function listFailedSoftys(): Promise<QueuedVisitaSoftys[]> {
  return conBaseLocal(async (db) =>
    (await db.getAll(STORE_PENDING_SOFTYS)).filter((i) => i.status === 'fallido'));
}

export async function pendingSoftysCount(): Promise<number> {
  return (await listPendingSoftys()).length;
}

/**
 * Las visitas de HOY que todavía están en el celular, para poder pintar el avance sin señal.
 *
 * Sin esto, en un mercado de treinta puestos y sin cobertura, la pantalla mostraría cero
 * registradas mientras la cola tiene doce: la persona creería que perdió el trabajo y volvería a
 * cargar los mismos puestos.
 */
export async function visitasSoftysLocalesDe(fecha: string): Promise<QueuedVisitaSoftys[]> {
  return conBaseLocal(async (db) =>
    (await db.getAll(STORE_PENDING_SOFTYS)).filter((i) => i.input.contexto.fecha === fecha));
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Sube lo pendiente. Mismas reglas que las otras colas: **la falta de red no gasta intentos**,
 * solo los rechazos definitivos del servidor. Una jornada sin señal no puede terminar con las
 * visitas marcadas como fallidas.
 */
export async function flushSoftys(): Promise<{ sent: number; remaining: number }> {
  // Toda la base se toca por `conBaseLocal`, NO por `getDb` suelto. Esta es la funcion que RESCATA
  // lo que quedo guardado en el telefono, asi que es justo la que no se puede caer cuando la
  // conexion se murio: si se cae, el trabajo queda adentro y nadie lo ve nunca.
  const items = await conBaseLocal(async (db) =>
    (await db.getAll(STORE_PENDING_SOFTYS)).filter((i) => i.status !== 'fallido'));
  if (items.length === 0) return { sent: 0, remaining: 0 };
  if (!uploader) {
    throw new Error('La cola de visitas de Softys no tiene enviador registrado (importa api/softys).');
  }

  let sent = 0;
  for (const item of items) {
    try {
      const fotoFachadaPath = item.fachadaBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'fachada', item.fachadaBlob)
        : null;
      const fotoPedidoPath = item.pedidoBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'pedido', item.pedidoBlob)
        : null;
      const fotoSalidaPath = item.salidaBlob
        ? await uploader.subirFotoVisitaSoftys(item.clientUuid, 'salida', item.salidaBlob)
        : null;

      // `clientUuid` se fuerza desde la CLAVE del store y no del payload guardado: es lo que hace
      // idempotente el reintento. Si los dos valores pudieran divergir, un reintento crearía una
      // visita duplicada — y una visita duplicada es un puesto contado dos veces en el reporte.
      await uploader.registrarVisitaSoftys({
        ...item.input,
        clientUuid: item.clientUuid,
        fotoFachadaPath,
        fotoPedidoPath,
        fotoSalidaPath,
      });
      await conBaseLocal((db) => db.delete(STORE_PENDING_SOFTYS, item.clientUuid));
      sent += 1;
    } catch (err) {
      const permanent = uploader.isPermanentError(err);
      const attempts = permanent ? item.attempts + 1 : 0;
      await conBaseLocal((db) => db.put(STORE_PENDING_SOFTYS, {
        ...item,
        attempts,
        lastError: errorMessage(err),
        status: permanent && attempts >= MAX_SOFTYS_ATTEMPTS ? 'fallido' : 'pendiente',
      }));
    }
  }

  const remaining = (await listPendingSoftys()).length;
  return { sent, remaining };
}
